import { Component, computed, inject, Input, OnDestroy, OnInit, viewChild } from '@angular/core';
import { FlowActionPanelAction } from '@components/flow-action-panel/flow-action-panel.component';
import {
    EFMarkerType,
    FCanvasChangeEvent,
    FCanvasComponent,
    FCreateConnectionEvent,
    FCreateNodeEvent,
    FFlowComponent,
    FMoveNodesEvent,
    FReassignConnectionEvent,
    FSelectionChangeEvent,
    FZoomDirective,
} from '@foblex/flow';
import { EOperationSystem, PlatformService } from '@foblex/platform';
import { FlowStore } from '@store/flow-store';
import { Vessel } from 'icreate/common/models/vessel';

@Component({
    selector: 'app-channels-logic',
    templateUrl: './channels-logic.component.html',
    styleUrl: './channels-logic.component.scss',
    standalone: false,
})
export class ChannelsLogicComponent implements OnInit {
    @Input() vessel: Vessel;
    @Input() userCanEdit: boolean = true;

    flowId: string;

    constructor() {}

    protected readonly store: any = inject(FlowStore);
    private readonly _platform = inject(PlatformService);

    private readonly _flow = viewChild(FFlowComponent);
    private readonly _canvas = viewChild(FCanvasComponent);
    private readonly _zoom = viewChild(FZoomDirective);

    private _isChangeAfterLoadedResetAndCenter = true;

    protected readonly fCanvasChangeEventDebounce = 200;

    protected readonly viewModel = computed(() => this.store.model());

    protected readonly nodes = this.store.nodes;
    protected readonly connections = this.store.connections;

    protected eMarkerType = EFMarkerType;

    public ngOnInit(): void {
        this.flowId = `Flow ${this.vessel.id}`;

        this.store.initialize(this.flowId);
        this._applySelection();
    }

    private _applySelection(): void {
        const model = this.store.model();
        if (model?.selection) {
            this._flow()?.select(model.selection.nodes || [], model.selection.connections || [], false);
        }
    }

    protected reset(): void {
        if (!this.userCanEdit) return;
        this._flow()?.reset();
        this.store.resetFlow();
        this._isChangeAfterLoadedResetAndCenter = true;
        this.store.initialize(this.flowId);
    }

    protected editorLoaded(): void {
        this._isChangeAfterLoadedResetAndCenter = true;
        if (this.viewModel()?.transform?.position) {
            return;
        }
        this._canvas()?.fitToScreen({ x: 150, y: 150 }, false);
    }

    protected changeCanvasTransform(event: FCanvasChangeEvent): void {
        if (this._isChangeAfterLoadedResetAndCenter) {
            this._isChangeAfterLoadedResetAndCenter = false;

            return;
        }
        this.store.transformCanvas(event);
    }

    protected createNode(event: FCreateNodeEvent): void {
        if (!this.userCanEdit) return;
        this.store.createNode(event);
    }

    protected moveNodes(event: FMoveNodesEvent): void {
        if (!this.userCanEdit) return;
        this.store.moveNodes(event);
    }

    protected createConnection(event: FCreateConnectionEvent): void {
        if (!this.userCanEdit) return;
        this.store.createConnection(event);
    }

    protected reassignConnection(event: FReassignConnectionEvent): void {
        if (!this.userCanEdit) return;
        this.store.reassignConnection(event);
    }

    protected changeSelection(event: FSelectionChangeEvent): void {
        if (!this.userCanEdit) return;
        this.store.changeSelection(event);
        this._applySelection();
    }

    protected removeConnectionByOutput(outputId: string): void {
        if (!this.userCanEdit) return;
        this.store.removeConnectionByOutput(outputId);
    }

    protected removeConnectionByInput(outputId: string): void {
        if (!this.userCanEdit) return;
        this.store.removeConnectionByInput(outputId);
    }

    private _selectAll(): void {
        this._flow()?.selectAll();
        this.store.selectAll();
    }

    public exportFlow(): void {
        const state = this._flow().getState();
        console.log(state.connections);
        console.log(state.nodes);
        console.log(state.groups);
    }

    protected processAction(event: FlowActionPanelAction): void {
        switch (event) {
            case FlowActionPanelAction.SELECT_ALL:
                this._selectAll();
                break;
            case FlowActionPanelAction.ZOOM_IN:
                this._zoom()?.zoomIn();
                break;
            case FlowActionPanelAction.ZOOM_OUT:
                this._zoom()?.zoomOut();
                break;
            case FlowActionPanelAction.FIT_TO_SCREEN:
                this._canvas()?.fitToScreen();
                break;
            case FlowActionPanelAction.ONE_TO_ONE:
                this._canvas()?.resetScaleAndCenter();
                break;
        }
    }

    protected onKeyDown(event: KeyboardEvent): void {
        if (!this.userCanEdit) return;
        if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
            return;
        }

        switch (event.key) {
            case 'Backspace':
            case 'Delete':
                this.store.removeSelected();
                break;
            case '+':
                if (this._isCommandButton(event)) {
                    this._zoom()?.zoomIn();
                }
                break;
            case '-':
                if (this._isCommandButton(event)) {
                    this._zoom()?.zoomOut();
                }
                break;
            case 'a':
            case 'A':
                if (this._isCommandButton(event)) {
                    this._selectAll();
                }
                break;
        }
    }

    private _isCommandButton(event: { metaKey: boolean; ctrlKey: boolean }): boolean {
        return this._platform.getOS() === EOperationSystem.MAC_OS ? event.metaKey : event.ctrlKey;
    }
}
